import {DeckSession} from '../core/deck-session';
import {DeckWorkspace} from '../core/deck-workspace';
import {PresenterBridge} from '../core/presenter-bridge';
import {RemoteControl} from './remote-control';
import {routeUrl, syncRoute} from '../core/routing';
import {SlideStage} from '../render/slide-stage';
import type {WorkspaceAsset} from '../core/model';
import {button, element, iconButton} from './dom';
import {ignoresPresentationHotkeys, isRepeatedPresentationHotkey} from './keyboard-navigation';
import {bindWheelNavigation} from './wheel-navigation';
import {toggleFullscreen} from './fullscreen';
import {AudienceVisibility} from './audience-visibility';
import {actionMenu} from './action-menu';
import {withIcon} from './workspace-icons';

export class SpeakerView {
  readonly element: HTMLElement;
  #workspace: DeckWorkspace;
  #session: DeckSession;
  #bridge: PresenterBridge;
  #remote!: RemoteControl;
  #room: string | undefined;
  /** The speaker view never draws the blackout; it carries the flag so the phone can set it. */
  #blackedOut = false;
  #currentStage: SlideStage;
  #nextStage: SlideStage;
  #notes: HTMLElement;
  #timer!: HTMLElement;
  #visibility!: AudienceVisibility;
  #editLink = withIcon(element('a', 'button button-quiet', 'Edit slides'), 'edit');
  #started = Date.now();
  #timerHandle = 0;

  constructor(
    workspace: DeckWorkspace,
    page: number,
    step: number,
    assets: WorkspaceAsset[] = [],
    room: string | null = null,
  ) {
    this.#room = room ?? undefined;
    this.#workspace = workspace;
    this.#session = new DeckSession(workspace.source, {page, step});
    this.#bridge = new PresenterBridge(workspace.deckId);
    this.element = element('main', 'speaker');
    const header = this.createHeader();
    const grid = element('div', 'speaker-grid');
    const currentPanel = element('section', 'speaker-stage-panel');
    currentPanel.append(element('span', 'speaker-label', 'Current'));
    const current = element('div', 'speaker-stage');
    currentPanel.append(current);
    const assetUrls = new Map(assets.map((asset) => [asset.name, asset.url]));
    const resolveAsset = (source: string) =>
      source.startsWith('assets/')
        ? assetUrls.get(source.slice(7)) ?? workspace.assetUrl(source)
        : source;
    this.#currentStage = new SlideStage(current, {deckId: workspace.deckId, resolveAsset});
    const nextPanel = element('section', 'speaker-stage-panel');
    nextPanel.append(element('span', 'speaker-label', 'Next'));
    const next = element('div', 'speaker-stage');
    nextPanel.append(next);
    this.#nextStage = new SlideStage(next, {deckId: workspace.deckId, resolveAsset});
    this.#notes = element('section', 'speaker-notes');
    grid.append(currentPanel, nextPanel, this.#notes);
    this.#remote = new RemoteControl({
      deckId: workspace.deckId,
      room,
      role: 'join',
      currentState: () => ({...this.#session.location, blackout: this.#blackedOut}),
      applyState: (state) => {
        this.#session.go(state.page, state.step);
        if (state.blackout !== this.#blackedOut) {
          this.setBlackout(state.blackout);
          // Keep any audience window on this machine in step with the phone.
          this.#bridge.sendBlackout(state.blackout);
        }
      },
      onRoomChange: (code) => {
        this.#room = code ?? undefined;
        syncRoute(
          'speaker',
          this.#workspace.deckId,
          this.#session.location.page,
          this.#session.location.step,
          code ?? undefined,
        );
      },
    });
    header.append(this.#remote.element);
    this.element.append(header, grid);
    this.bind();
    this.render();
    this.#timerHandle = window.setInterval(() => this.updateTimer(), 1_000);
  }

  private createHeader(): HTMLElement {
    const header = element('header', 'speaker-header');
    const identity = element('div', 'speaker-identity');
    const brand = element('a', 'brand', 'Slides');
    brand.href = '/';
    const description = element('div', 'speaker-deck-identity');
    description.append(element('strong', '', this.#session.deck.title), element('span', '', 'Speaker view'));
    this.#visibility = new AudienceVisibility(() => this.toggleBlackout());
    description.append(this.#visibility.status);
    identity.append(brand, description);
    const controls = element('div', 'speaker-controls');
    const back = iconButton('Previous', 'previous');
    back.addEventListener('click', () => this.command('backward'));
    const next = iconButton('Next', 'next');
    next.addEventListener('click', () => this.command('forward'));
    const audience = withIcon(button('Present', 'button button-primary speaker-audience'), 'present');
    audience.ariaLabel = 'Open audience window';
    audience.addEventListener('click', () => {
      window.open(
        routeUrl(
          'present',
          this.#workspace.deckId,
          this.#session.location.page,
          this.#session.location.step,
          this.#room,
        ),
        'slides2-audience',
      );
    });
    const fullscreen = iconButton('Fullscreen', 'fullscreen');
    fullscreen.addEventListener('click', () => void toggleFullscreen());
    this.#timer = element('time', 'speaker-timer', '00:00');
    controls.append(back, next, audience, fullscreen, actionMenu('More', [this.#editLink, this.#visibility.action], 'more'));
    header.append(identity, controls, this.#timer);
    return header;
  }

  private bind(): void {
    bindWheelNavigation(this.element, (step) => this.command(step));
    document.addEventListener('keydown', (event) => {
      if (ignoresPresentationHotkeys(event)) return;
      if (isRepeatedPresentationHotkey(event)) {
        event.preventDefault();
        return;
      }
      if (event.key === ' ' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.command('forward');
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.command('backward');
      }
      if (event.key.toLowerCase() === 'b') this.toggleBlackout();
    });
    this.#session.onChange(() => {
      this.render();
      this.#bridge.sendLocation(this.#session.location);
      this.#remote.publishLocal();
    });
    this.#bridge.listen((message) => {
      if (message.type === 'blackout') this.setBlackout(message.value);
      if (message.type === 'location') this.#session.go(message.location.page, message.location.step);
    });
    this.#bridge.requestBlackout();
  }

  private toggleBlackout(): void {
    this.setBlackout(!this.#blackedOut);
    this.#bridge.sendBlackout(this.#blackedOut);
    this.#remote.publishLocal();
  }

  private setBlackout(blacked: boolean): void {
    this.#blackedOut = blacked;
    this.#currentStage.setMediaSuspended(blacked);
    this.#nextStage.setMediaSuspended(blacked);
    this.#visibility.sync(blacked);
  }

  private command(command: 'forward' | 'backward'): void {
    if (command === 'forward') this.#session.forward();
    else this.#session.backward();
    // The local session change already broadcasts its resulting location.
    // Sending a second relative command made the audience advance twice:
    // once to that location and once again for the command.
  }

  private render(): void {
    const {page, step} = this.#session.location;
    const slide = this.#session.deck.slides[page - 1];
    const next = this.#session.deck.slides[page] ?? slide;
    if (!slide || !next) return;
    const context = {
      chains: this.#session.deck.chains,
      language: this.#session.deck.language,
      theme: this.#session.deck.theme,
      style: this.#session.deck.style,
    };
    this.#currentStage.render(slide, step, context);
    this.#nextStage.render(next, 0, context);
    this.#currentStage.pause();
    this.#nextStage.pause();
    const heading = element('div', 'speaker-note-heading');
    heading.append(element('span', '', String(slide.meta.section ?? '')), element('strong', '', `${page} / ${this.#session.deck.slides.length}`));
    this.#notes.replaceChildren(heading, element('h2', '', slide.title), element('div', 'speaker-note-copy', slide.notes || 'No speaker notes for this slide.'));
    syncRoute('speaker', this.#workspace.deckId, page, step, this.#room);
    this.#editLink.href = routeUrl('edit', this.#workspace.deckId, page, step);
  }

  private updateTimer(): void {
    const seconds = Math.floor((Date.now() - this.#started) / 1_000);
    const minutes = Math.floor(seconds / 60);
    this.#timer.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }
}
